import React, { useState } from 'react'
import { t } from '@lingui/macro'
import * as Sentry from '@sentry/gatsby'

import { useUserAccount } from 'gatsby-plugin-8fit-user-account'
import Checkbox from 'components/styled-checkbox'
import { i18n } from 'utils/i18n'
import Input from 'components/input'
import { classNames } from 'utils/dom'

import {
  sectionCheckbox,
  sectionInput,
  checkboxLabel,
  checkbox,
  inputsRow,
  valid,
  loading,
} from './VoucherInput.module.scss'
import {
  VoucherInputProps,
  VoucherInputState,
  voucherValidateFn,
  DiscountData,
} from './types'
import { deriveVoucherState } from './lib/voucherstate'

export const validateDiscountCode = (
  validateApiFn: (
    code: string
  ) => Promise<Pick<EF.DiscountInformation, 'percent_off'> | null>
) => async (code: string) => {
  try {
    const responseBody = await validateApiFn(code)
    if (responseBody?.percent_off) {
      const { percent_off } = responseBody
      return {
        percent_off,
        code,
      } as DiscountData
    }
  } catch (e) {
    Sentry.captureException(e)
  }
  return null
}

const VoucherInput = (props: VoucherInputProps) => {
  const { prefilledData, onReceiveDiscountInformation, className } = props
  const [voucherCode, setVoucherCode] = useState('')
  const [voucherState, setVoucherState] = useState<VoucherInputState>(
    !!prefilledData && !voucherCode
      ? VoucherInputState.Prefilled
      : VoucherInputState.None
  )
  const { validateDiscountCode: validateDiscountCodeApiFn } = useUserAccount()
  const validateVoucherFunction: voucherValidateFn = validateDiscountCode(
    validateDiscountCodeApiFn
  )

  const [
    isCheckboxShown,
    isCheckboxChecked,
    isControlsLocked,
    isSuccessShown,
    inputValue,
  ] = deriveVoucherState(voucherState, prefilledData, voucherCode)

  const validate = async () => {
    if (!isControlsLocked && voucherCode) {
      setVoucherState(VoucherInputState.Loading)
      const data = await validateVoucherFunction(voucherCode)
      if (data) {
        setVoucherState(VoucherInputState.Valid)
        onReceiveDiscountInformation(data)
      } else {
        setVoucherState(VoucherInputState.Invalid)
      }
    }
  }

  return (
    <div className={className}>
      <fieldset
        className={sectionCheckbox}
        hidden={!isCheckboxShown}
        disabled={isControlsLocked}
      >
        <label className={checkboxLabel}>
          <Checkbox
            name="has-voucher"
            className={checkbox}
            checked={isCheckboxChecked}
            disabled={isControlsLocked}
            onChange={() => {
              if (!isControlsLocked) {
                setVoucherState(
                  isCheckboxChecked
                    ? VoucherInputState.None
                    : VoucherInputState.EnterVoucher
                )
              }
            }}
          />
          {i18n._(t`I have a discount voucher code`)}
        </label>
      </fieldset>
      {voucherState !== VoucherInputState.None && (
        <fieldset className={sectionInput} disabled={isControlsLocked}>
          {/**
            The auto focus applies once the user checks the checkmark.
            After reading into the issue I believe that this isn't an a11y
            issue in this use case. That's why I'm explicitly using it here.
            Also, eslint-disable-line and eslint-disable-next-line didn't work.
            So, I'm using this weird eslint-disable / *-enable block.
          **/}
          {/* eslint-disable jsx-a11y/no-autofocus */}
          <Input
            errorMessage={
              voucherState === VoucherInputState.Invalid
                ? i18n._(t`is invalid`)
                : ''
            }
            label={i18n._(t`Voucher code`)}
            value={inputValue}
            disabled={isControlsLocked}
            onChange={(e) => {
              setVoucherCode(e.target.value)
              setVoucherState(VoucherInputState.EnterVoucher)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                validate()
              }
            }}
            onBlur={validate}
            classNameControlsRow={classNames([
              inputsRow,
              isSuccessShown && valid,
              voucherState === VoucherInputState.Loading && loading,
            ])}
            autoFocus={!isControlsLocked}
            autoComplete="off"
          />
          {/* eslint-enable jsx-a11y/no-autofocus */}
        </fieldset>
      )}
    </div>
  )
}

export default VoucherInput
